import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { ICONS } from 'consts/consts';
import { isEqual } from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { useCurrentOrganization } from 'utils/util/hooks';
import { formatDateWithUnicode, getDatePart } from 'utils/util/time';
import {
  autoDownloadLink,
  getPrettyDate,
  sanitizeUrlQueryParams,
} from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import { AdvancedCurrentPeopleRoleOneOnOnesAdminApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { sanitizeApiFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_GROUP } from 'views/Widgets/People/Filters/common';
import RevealLink from 'views/Widgets/RevealLink';

interface AgendaItemResponse {
  id: number;
  owner_person: Record<string, unknown>;
  participant_person: Record<string, unknown>;
  description: string | null;
  created_at: string;
  completed_at: string | null;
}

const OneOnOnesAdminPage: FC = () => {
  const { formatMessage, locale } = useIntl();
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);

  const [currentUrl, setCurrentUrl] = useState<string>('admin/agenda-items');
  const [currentTotalCount, setCurrentTotalCount] = useState<
    number | undefined
  >(undefined);
  const [currentParams, setCurrentParams] = useState<{
    filter_target_audience?: string;
    start_date?: string;
    end_date?: string;
  }>({});
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [accumulatedData, setAccumulatedData] = useState<
    AgendaItemResponse[] | undefined
  >(undefined);
  const [downloadingCSVInProgress, setDownloadingCSVInProgress] =
    useState(false);
  const currentOrganization = useCurrentOrganization();

  const callback = useCallback(
    (
      data: {
        results: AgendaItemResponse[];
        next?: string;
        count: number;
      } | null,
      error: unknown,
      hardError: unknown
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching agenda items data:', error || hardError);
        return;
      }
      setAccumulatedData((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setCurrentTotalCount(data.count);
      setIsLoadingMoreItems(false);
    },
    []
  );

  const { error } = useConfirmApi<{
    results: AgendaItemResponse[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    params: currentParams,
    callback: callback,
  });

  const [csvTaskDescriptor, setCsvTaskDescriptor] = useState<
    { task_key: string; status: string } | undefined
  >(undefined);

  const handleCsvExportError = useCallback(
    (error: object) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      console.error('Error downloading CSV:', error);
      toast.error(
        formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones_admin.error_generating_csv',
          defaultMessage: 'Error generating CSV',
        })
      );
    },
    [formatMessage]
  );

  const handleCsvExportCompleted = useCallback(
    (output: BlobPart) => {
      setDownloadingCSVInProgress(false);
      setCsvTaskDescriptor(undefined);
      autoDownloadLink({
        data: output,
        filename: `one_on_ones_${
          currentOrganization?.name ?? 'confirm'
        }_${formatDateWithUnicode(new Date(), 'yyyy_MM_dd_hh_mm_ss')}.csv`,
        mime: 'text/csv',
      });
    },
    [currentOrganization?.name]
  );

  useBackgroundTask({
    taskDescriptor: csvTaskDescriptor,
    onTaskCompleted: handleCsvExportCompleted,
    onError: handleCsvExportError,
  });

  const onCsvExportSubmitted = useCallback(
    (data: { task_key?: string } | null, error) => {
      if (error || !data?.task_key) {
        handleCsvExportError(error);
        return;
      }
      setCsvTaskDescriptor({ task_key: data.task_key, status: 'submitted' });
    },
    [handleCsvExportError]
  );

  const handleOnDownloadCSV = useCallback(() => {
    setDownloadingCSVInProgress(true);
  }, []);

  // This request only enqueues the export; polling delivers the file.
  useConfirmApi({
    method: 'GET',
    url: sanitizeUrlQueryParams(currentUrl),
    params: {
      ...currentParams,
      page: undefined,
      media_type: 'text/csv',
    },
    disabled: !downloadingCSVInProgress || !!csvTaskDescriptor,
    callback: onCsvExportSubmitted,
  });

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.table.column.participants',
          defaultMessage: 'Participants',
        }),
        field: 'participants',
        format: FORMAT_AVATAR_GROUP,
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.table.column.date',
          defaultMessage: 'Date',
        }),
        field: 'created_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            locale,
            includeTime: false,
          });
        },
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.table.column.completed',
          defaultMessage: 'Completed',
        }),
        field: 'completed_at',
        format: (date: string | null) => {
          if (!date) return '';
          return getPrettyDate({
            dateString: date,
            locale,
            includeTime: false,
          });
        },
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.table.column.content',
          defaultMessage: 'Content',
        }),
        field: 'description',
        format: (description: string) => {
          return <RichTextViewer model={description} expanded={true} />;
        },
        className: 'col-6',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    if (!accumulatedData) {
      return undefined;
    }
    return accumulatedData.map((item) => {
      return {
        id: item.id,
        participants: [item.owner_person, item.participant_person],
        created_at: item.created_at,
        completed_at: item.completed_at,
        description: item.description ?? '',
      };
    });
  }, [accumulatedData]);

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl: string) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  const handleFilteringCallback = useCallback(() => {}, []);

  const handleOnChangeFilter = useCallback(
    (object) => {
      const { filter_target_audience, start_date, end_date } = object;
      // The filter editor keeps blank conditions around while the user fills
      // them in; the backend rejects those, so only send what is complete.
      const sanitizedFilter = sanitizeApiFilter(filter_target_audience);
      const params = {
        filter_target_audience: sanitizedFilter
          ? JSON.stringify(sanitizedFilter)
          : undefined,
        start_date: start_date ? getDatePart(start_date) : undefined,
        end_date: end_date ? getDatePart(end_date) : undefined,
      };
      if (!isEqual(currentParams, params)) {
        setIsLoadingMoreItems(false);
        setAccumulatedData(undefined);
        setCurrentParams(params);
        setCurrentUrl('admin/agenda-items');
        setNextUrl(undefined);
      }
    },
    [currentParams]
  );

  const renderInputs = useCallback(
    ([filterTargetAudience, startDate, endDate]: ReactNode[]) => {
      return (
        <Row>
          <Col className="col-10">
            <Row>
              <Col className="ps-0">{filterTargetAudience}</Col>
              <Col className="col-auto">
                <div className="text-muted mt-2 pt-1">
                  <i className="fe fe-filter me-2" />
                </div>
              </Col>
            </Row>
            <Row>
              <RevealLink
                className="ps-0 ms-0 mt-n3 mb-2 pb-2 pt-0"
                text={formatMessage({
                  id: 'app.views.administration.content_administration.one_on_ones.search.reveal_link.additional_search_options',
                  defaultMessage: 'Additional search options...',
                })}
              >
                <Col className="ps-0">{startDate}</Col>
                <Col className="ps-0">{endDate}</Col>
              </RevealLink>
            </Row>
          </Col>
          <Col className="col-2">
            {!!currentTotalCount && (
              <div className="float-right">
                <Button
                  onClick={handleOnDownloadCSV}
                  className="btn btn-sm btn-light float-end"
                >
                  <i className={ICONS.EXPORT + ' me-2'} />
                  <FormattedMessage
                    id="app.views.administration.content_administration.one_on_ones_admin.page.export_to_csv"
                    defaultMessage="Export {count, plural, one {# item} other {# items}} to CSV"
                    values={{ count: currentTotalCount }}
                  />
                </Button>
              </div>
            )}
          </Col>
        </Row>
      );
    },
    [currentTotalCount, formatMessage, handleOnDownloadCSV]
  );
  const actions = useMemo(() => {
    return (
      <Card>
        <CardBody className="pt-4 pb-0 mb-0">
          <ValidatedForm
            renderInputs={renderInputs}
            buttonColor="light"
            hideSubmitButton={true}
            disableUnsavedChangesPrompt={true}
            onChange={handleOnChangeFilter}
            callback={handleFilteringCallback}
            inputs={[
              {
                name: 'filter_target_audience',
                type: INPUT_TYPES.CUSTOM_COMPONENT,
                component: AdvancedCurrentPeopleRoleOneOnOnesAdminApiComponent,
              },
              {
                name: 'start_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.one_on_ones.search.start_date.helper_text',
                  defaultMessage: 'Start date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
              {
                name: 'end_date',
                helperText: formatMessage({
                  id: 'app.views.administration.content_administration.one_on_ones.search.end_date.helper_text',
                  defaultMessage: 'End date',
                }),
                type: INPUT_TYPES.DATE_PICKER,
              },
            ]}
          />
        </CardBody>
      </Card>
    );
  }, [
    renderInputs,
    handleOnChangeFilter,
    handleFilteringCallback,
    formatMessage,
  ]);

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <>
      <Loading
        active={!rows}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.loading.message',
          defaultMessage: 'Loading data...',
        })}
      />
      <Loading
        active={downloadingCSVInProgress}
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.one_on_ones.loading.message.downloading_csv',
          defaultMessage: 'Downloading CSV, please wait...',
        })}
      />
      {actions}
      {currentTotalCount !== undefined && currentTotalCount === 0 && (
        <div className="text-muted text-center mt-4">
          {formatMessage({
            id: 'app.views.administration.content_administration.one_on_ones.empty',
            defaultMessage: 'No 1:1 agenda items found.',
          })}
        </div>
      )}
      {!!rows && rows.length > 0 && (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
        />
      )}
      <div ref={loadMoreRef} />
      {isLoadingMoreItems && <Loading />}
    </>
  );
};

export default React.memo(OneOnOnesAdminPage);
